
import { useState, type DragEvent } from "react";
import { Button, Input, Col, Row, Empty } from "antd";
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    PlusOutlined,
    CloseOutlined,
    MenuOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import moment from "moment";

import { Client } from "../../core/api-client";
import {
    API_GET_PROJECT_LIST,
    ROUTE_MANAGE_PROJECT,
} from "../../shared/routes/private.routes";
import { showNotification } from "../../shared/utils/utils";

import "./export-project.less";

const PROJECT_EXPORT_COLUMNS = [
    { key: "entityName", label: "Entity" },
    { key: "sowNumber", label: "SOW / Quotation" },
    { key: "poNumber", label: "PO Number" },
    { key: "projectName", label: "Project Name" },
    { key: "l2ProjectName", label: "Project" },
    { key: "projectTypeName", label: "Project Type" },
    { key: "startDate", label: "Start Date" },
    { key: "endDate", label: "End Date" },
    { key: "poCurrencyName", label: "Currency" },
    { key: "poValue", label: "PO Value" },
    { key: "margin", label: "Margin" },
    { key: "status", label: "Status" },
    { key: "projectManagerName", label: "Project Manager" },
    { key: "projectManagerEmail", label: "Project Manager Email" },
    { key: "accountManagerName", label: "Account Manager" },
    { key: "accountName", label: "Client" },
    { key: "employeeNames", label: "Assigned Employees" },
];

type DragSource = "available" | "active";

const ExportProjectPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Filters and sorting received from Manage Project page.
    const filters = location.state?.filters || {};

    const sorter = location.state?.sorter || {
        sort_by: "",
        sort_type: "DESC",
    };

    const [activeExportColumns, setActiveExportColumns] = useState<string[]>(
        PROJECT_EXPORT_COLUMNS.map((column) => column.key)
    );

    const [availableSearch, setAvailableSearch] = useState("");
    const [activeSearch, setActiveSearch] = useState("");
    const [exporting, setExporting] = useState(false);

    const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(
        null
    );

    const [dragSource, setDragSource] = useState<DragSource | null>(null);

    const availableExportColumns = PROJECT_EXPORT_COLUMNS.filter(
        (column) => !activeExportColumns.includes(column.key)
    );

    const filteredAvailableExportColumns = availableExportColumns.filter(
        (column) =>
            column.label.toLowerCase().includes(availableSearch.toLowerCase())
    );

    const filteredActiveExportColumns = activeExportColumns
        .map((key) =>
            PROJECT_EXPORT_COLUMNS.find((column) => column.key === key)
        )
        .filter(
            (column): column is (typeof PROJECT_EXPORT_COLUMNS)[number] =>
                Boolean(
                    column &&
                    column.label.toLowerCase().includes(activeSearch.toLowerCase())
                )
        );

    const addExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.includes(key) ? previous : [...previous, key]
        );
    };

    const removeExportColumn = (key: string) => {
        setActiveExportColumns((previous) =>
            previous.filter((columnKey) => columnKey !== key)
        );
    };

    const clearDragState = () => {
        setDraggedColumnKey(null);
        setDragSource(null);
    };

    const handleDragStart = (
        event: DragEvent<HTMLDivElement>,
        key: string,
        source: DragSource
    ) => {
        setDraggedColumnKey(key);
        setDragSource(source);

        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", key);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDropOnActive = (
        event: DragEvent<HTMLDivElement>,
        targetKey?: string
    ) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (!sourceKey) {
            clearDragState();
            return;
        }

        setActiveExportColumns((previous) => {
            const updated = [...previous];

            if (dragSource === "available") {
                if (updated.includes(sourceKey)) return previous;

                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length;

                if (targetIndex === -1) {
                    return [...updated, sourceKey];
                }

                updated.splice(targetIndex, 0, sourceKey);
                return updated;
            }

            if (dragSource === "active") {
                const sourceIndex = updated.indexOf(sourceKey);
                const targetIndex = targetKey
                    ? updated.indexOf(targetKey)
                    : updated.length - 1;

                if (sourceIndex === -1 || targetIndex === -1) {
                    return previous;
                }

                const [movedColumn] = updated.splice(sourceIndex, 1);
                updated.splice(targetIndex, 0, movedColumn);

                return updated;
            }

            return previous;
        });

        clearDragState();
    };

    const handleDropOnAvailable = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        const sourceKey =
            draggedColumnKey || event.dataTransfer.getData("text/plain");

        if (sourceKey && dragSource === "active") {
            removeExportColumn(sourceKey);
        }

        clearDragState();
    };

    const selectAllExportColumns = () => {
        setActiveExportColumns(
            PROJECT_EXPORT_COLUMNS.map((column) => column.key)
        );
    };

    const removeAllExportColumns = () => {
        setActiveExportColumns([]);
    };

    const formatDate = (value: any) => {
        return value ? moment(value).format("DD-MMM-YYYY") : "";
    };

    const mapProjectRow = (item: any) => ({
        entityName: item?.entityName ?? "",
        sowNumber: item?.sowNumber ?? "",
        poNumber: item?.poNumber ?? "",
        projectName: item?.projectName ?? "",
        l2ProjectName: item?.l2ProjectName ?? "",
        projectTypeName: item?.projectTypeName ?? "",
        startDate: formatDate(item?.startDate),
        endDate: formatDate(item?.endDate),
        poCurrencyName: item?.poCurrencyName ?? "",
        poValue: item?.poValue ?? "",
        margin: item?.margin ?? "",
        status: item?.status ?? "",
        projectManagerName: item?.projectManagerName ?? "",
        projectManagerEmail: item?.projectManagerEmail ?? "",
        accountManagerName: item?.accountManagerName ?? "",
        accountName: item?.accountName ?? "",
        employeeNames: Array.isArray(item?.employeeNames)
            ? item.employeeNames.join(", ")
            : "",
    });

    const exportRows = async () => {
        if (activeExportColumns.length === 0) {
            showNotification("Please select at least one column", "error");
            return;
        }

        setExporting(true);

        try {
            // Remove empty filter values before sending the request.
            const exportFilters = Object.fromEntries(
                Object.entries(filters).filter(
                    ([, value]) =>
                        value !== undefined &&
                        value !== null &&
                        value !== "" &&
                        !(Array.isArray(value) && value.length === 0)
                )
            );

            // No page or limit: the Project API supports unpaginated results.
            const response = await Client.post(
                API_GET_PROJECT_LIST,
                exportFilters,
                {
                    params: {
                        sort_by: sorter.sort_by || "",
                        sort_type: sorter.sort_type || "DESC",
                    },
                }
            );

            const rows = response?.data?.data;

            if (!Array.isArray(rows)) {
                throw new Error("Invalid Project API response");
            }

            const selectedColumns = activeExportColumns
                .map((key) =>
                    PROJECT_EXPORT_COLUMNS.find((column) => column.key === key)
                )
                .filter(
                    (column): column is (typeof PROJECT_EXPORT_COLUMNS)[number] =>
                        Boolean(column)
                );

            const mappedRows = rows.map(mapProjectRow);

            const csvRows = mappedRows.map((row: any) =>
                Object.fromEntries(
                    selectedColumns.map((column) => [
                        column.label,
                        row[column.key] ?? "",
                    ])
                )
            );

            const csv = Papa.unparse(csvRows, {
                escapeFormulae: true,
                columns: selectedColumns.map((column) => column.label),
            });

            const blob = new Blob(["\uFEFF", csv], {
                type: "text/csv;charset=utf-8;",
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = "project_list.csv";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

            showNotification(
                `Successfully exported ${rows.length} Project records`,
                "success"
            );
        } catch (error) {
            console.error("Project export failed:", error);
            showNotification("Unable to export Project records", "error");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="app-page project-export-page">
            <div className="it-export-page-header">
                <div className="it-export-page-heading">
                    <div className="it-export-page-icon">
                        <DownloadOutlined />
                    </div>

                    <div>
                        <h1>Export Project</h1>
                        <p>
                            Select, organize and customize the columns for your export.
                        </p>
                    </div>
                </div>

                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(ROUTE_MANAGE_PROJECT)}
                >
                    Back to Manage Project
                </Button>
            </div>

            <div className="it-export-screen">
                <Row gutter={[20, 20]} className="it-export-panels">
                    {/* AVAILABLE COLUMNS */}
                    <Col xs={24} md={12}>
                        <div className="it-export-panel">
                            <div className="it-export-panel-header">
                                <div>
                                    <h3>Available Columns</h3>
                                    <span>{availableExportColumns.length} columns</span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={selectAllExportColumns}
                                    disabled={availableExportColumns.length === 0}
                                >
                                    Select All
                                </Button>
                            </div>

                            <div className="it-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search available columns..."
                                    value={availableSearch}
                                    onChange={(event) =>
                                        setAvailableSearch(event.target.value)
                                    }
                                />
                            </div>

                            <div
                                className="it-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={handleDropOnAvailable}
                            >
                                {filteredAvailableExportColumns.length > 0 ? (
                                    filteredAvailableExportColumns.map((column) => (
                                        <div
                                            className={`it-export-column-item ${draggedColumnKey === column.key
                                                    ? "it-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "available")
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="it-export-drag-icon" />

                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="it-export-action-button"
                                                icon={<PlusOutlined />}
                                                title={`Add ${column.label}`}
                                                onClick={() => addExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="it-export-empty">
                                        {availableExportColumns.length === 0 ? (
                                            <>
                                                <CheckCircleOutlined className="it-export-empty-icon" />
                                                <h4>All columns selected</h4>
                                                <p>
                                                    All available columns are already included in your
                                                    export.
                                                </p>
                                            </>
                                        ) : (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No matching columns"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* ACTIVE COLUMNS */}
                    <Col xs={24} md={12}>
                        <div className="it-export-panel">
                            <div className="it-export-panel-header">
                                <div>
                                    <h3>Active Columns</h3>
                                    <span>
                                        {activeExportColumns.length} columns selected
                                    </span>
                                </div>

                                <Button
                                    type="link"
                                    onClick={removeAllExportColumns}
                                    disabled={activeExportColumns.length === 0}
                                >
                                    Remove All
                                </Button>
                            </div>

                            <div className="it-export-panel-search">
                                <Input
                                    allowClear
                                    placeholder="Search active columns..."
                                    value={activeSearch}
                                    onChange={(event) => setActiveSearch(event.target.value)}
                                />
                            </div>

                            <div
                                className="it-export-column-list"
                                onDragOver={handleDragOver}
                                onDrop={(event) => handleDropOnActive(event)}
                            >
                                {filteredActiveExportColumns.length > 0 ? (
                                    filteredActiveExportColumns.map((column) => (
                                        <div
                                            className={`it-export-column-item ${draggedColumnKey === column.key
                                                    ? "it-export-column-dragging"
                                                    : ""
                                                }`}
                                            key={column.key}
                                            draggable
                                            onDragStart={(event) =>
                                                handleDragStart(event, column.key, "active")
                                            }
                                            onDragOver={handleDragOver}
                                            onDrop={(event) =>
                                                handleDropOnActive(event, column.key)
                                            }
                                            onDragEnd={clearDragState}
                                        >
                                            <MenuOutlined className="it-export-drag-icon" />

                                            <span>{column.label}</span>

                                            <Button
                                                type="text"
                                                className="it-export-action-button"
                                                icon={<CloseOutlined />}
                                                title={`Remove ${column.label}`}
                                                onClick={() => removeExportColumn(column.key)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="it-export-empty">
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description="Drag columns here or add them from Available Columns"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* FOOTER */}
                <div className="it-export-footer">
                    <div className="it-export-selection-summary">
                        <span className="it-export-selection-count">
                            {activeExportColumns.length}
                        </span>

                        <span>
                            {activeExportColumns.length === 1
                                ? "column selected"
                                : "columns selected"}
                        </span>
                    </div>

                    <div className="it-export-footer-actions">
                        <Button
                            onClick={() => navigate(ROUTE_MANAGE_PROJECT)}
                            disabled={exporting}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={activeExportColumns.length === 0}
                            loading={exporting}
                            onClick={exportRows}
                        >
                            Export CSV
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportProjectPage;
